// Tipos y helpers de la pestaña "Prefiltrado" (CYBER-89). Espejo de
// cyber-service src/shared/interfaces/IPrefilter.ts.

export type PrefilterGender = "TODOS" | "MASCULINO" | "FEMENINO" | "CUOTA";
export type PrefilterBucket = "CUMPLE" | "SIN_CARGO" | "INCOMPLETO" | "NO_CUMPLE";

export interface IPrefilterSelection {
  prefilterId: string;
  bucket: PrefilterBucket;
}

export interface IPrefilterCounts {
  prefilterId: string;
  total: number;
  meets: number;
  withoutPosition: number;
  incomplete: number;
  fails: number;
  failsLocality: number;
  failsAge: number;
  failsGender: number;
  failsVisa: number;
  failsPosition: number;
  failsShift: number;
  meetsMale: number;
  meetsFemale: number;
}

export interface IPrefilter {
  prefilterId: string;
  clientId: string;
  client: string | null;
  campaign: string | null;
  name: string;
  ageMin: number | null;
  ageMax: number | null;
  gender: PrefilterGender;
  maleQuota: number | null;
  requireVisa: boolean;
  shiftExclusive: boolean;
  workday: string | null;
  schedule: string | null;
  notes: string | null;
  applicationsFrom: string | null;
  order: number;
  active: boolean;
  localities: { localityId: number; name: string }[];
  positions: string[];
  shifts: string[];
  updatedAt: string | null;
  counts: IPrefilterCounts | null;
}

export interface IPrefilterSummaryResponse {
  /** Solo las cuentas habilitadas en cyber-service (PREFILTER_EDITORS) crean, editan y duplican. */
  canEdit: boolean;
  prefilters: IPrefilter[];
}

/** Lo que manda el editor (sin prefilterId = crear). */
export interface IPrefilterDraft {
  prefilterId?: string;
  clientId: string;
  campaign: string;
  name: string;
  ageMin: string;
  ageMax: string;
  gender: PrefilterGender;
  maleQuota: string;
  requireVisa: boolean;
  shiftExclusive: boolean;
  workday: string;
  schedule: string;
  notes: string;
  applicationsFrom: string;
  order: string;
  active: boolean;
  localityIds: number[];
  positions: string[];
  shifts: string[];
}

export const BUCKET_LABELS: Record<PrefilterBucket, { label: string; help: string }> = {
  CUMPLE: {
    label: "Cumplen",
    help: "Personas disponibles que cumplen todas las condiciones del prefiltro.",
  },
  SIN_CARGO: {
    label: "Sin cargo",
    help: "Cumplen todo, pero no eligieron cargo (formularios antiguos no lo pedían). No se descartan.",
  },
  INCOMPLETO: {
    label: "Datos incompletos",
    help: "Sin comuna o sin fecha de nacimiento: no se pueden evaluar.",
  },
  NO_CUMPLE: {
    label: "No cumplen",
    help: "Fallan al menos una condición.",
  },
};

export interface IClientGroup {
  clientId: string;
  client: string;
  prefilters: IPrefilter[];
  /** Personas disponibles del cliente (el mayor total de sus prefiltros vigentes). */
  available: number | null;
}

/** Agrupa por cliente, en orden alfabético; dentro, por orden y nombre de la sede. */
export function groupByClient(prefilters: IPrefilter[]): IClientGroup[] {
  const groups = new Map<string, IClientGroup>();
  for (const prefilter of prefilters) {
    const group = groups.get(prefilter.clientId) ?? {
      clientId: prefilter.clientId,
      client: prefilter.client ?? prefilter.clientId,
      prefilters: [],
      available: null,
    };
    group.prefilters.push(prefilter);
    if (prefilter.counts) {
      group.available = Math.max(group.available ?? 0, prefilter.counts.total);
    }
    groups.set(prefilter.clientId, group);
  }
  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      prefilters: [...group.prefilters].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name, "es")),
    }))
    .sort((a, b) => a.client.localeCompare(b.client, "es"));
}

const normalize = (value: string) =>
  value.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/**
 * Buscador de la pestaña: cada palabra debe aparecer en algún campo del
 * prefiltro (cliente, sede, campaña, comunas, cargos, turnos, jornada,
 * horarios, observaciones, género). Sin distinguir tildes ni mayúsculas.
 */
export function matchesSearch(prefilter: IPrefilter, term: string): boolean {
  const words = normalize(term).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = normalize([
    prefilter.client ?? "",
    prefilter.clientId,
    prefilter.name,
    prefilter.campaign ?? "",
    prefilter.workday ?? "",
    prefilter.schedule ?? "",
    prefilter.notes ?? "",
    describeGender(prefilter),
    ...prefilter.localities.map((l) => l.name),
    ...prefilter.positions,
    ...prefilter.shifts,
  ].join(" | "));
  return words.every((word) => haystack.includes(word));
}

export function describeAge(prefilter: Pick<IPrefilter, "ageMin" | "ageMax">): string {
  const { ageMin, ageMax } = prefilter;
  if (ageMin !== null && ageMax !== null) return `${ageMin} a ${ageMax} años`;
  if (ageMin !== null) return `Desde ${ageMin} años`;
  if (ageMax !== null) return `Hasta ${ageMax} años`;
  return "Sin restricción";
}

export function describeGender(prefilter: Pick<IPrefilter, "gender" | "maleQuota">): string {
  switch (prefilter.gender) {
    case "MASCULINO": return "Solo masculino";
    case "FEMENINO": return "Solo femenino";
    case "CUOTA": return `Meta ${prefilter.maleQuota ?? 0}% H · ${100 - (prefilter.maleQuota ?? 0)}% M`;
    default: return "Mixto";
  }
}

/** % de hombres entre quienes cumplen; null si nadie cumple. */
export function currentMaleShare(counts: IPrefilterCounts | null): number | null {
  if (!counts) return null;
  const known = counts.meetsMale + counts.meetsFemale;
  if (known === 0) return null;
  return Math.round((counts.meetsMale / known) * 100);
}

/** Motivos de "no cumple" con conteo distinto de cero, del más frecuente al menos. */
export function failureReasons(counts: IPrefilterCounts | null): { label: string; value: number }[] {
  if (!counts) return [];
  return [
    { label: "comuna", value: counts.failsLocality },
    { label: "edad", value: counts.failsAge },
    { label: "género", value: counts.failsGender },
    { label: "visa", value: counts.failsVisa },
    { label: "cargo", value: counts.failsPosition },
    { label: "turno", value: counts.failsShift },
  ]
    .filter((reason) => reason.value > 0)
    .sort((a, b) => b.value - a.value);
}

const toText = (value: number | null) => (value === null || value === undefined ? "" : String(value));

export function draftFromPrefilter(prefilter: IPrefilter | null, asCopy = false): IPrefilterDraft {
  if (!prefilter) {
    return {
      clientId: "",
      campaign: "",
      name: "",
      ageMin: "18",
      ageMax: "50",
      gender: "TODOS",
      maleQuota: "",
      requireVisa: true,
      shiftExclusive: false,
      workday: "",
      schedule: "",
      notes: "",
      applicationsFrom: "",
      order: "0",
      active: true,
      localityIds: [],
      positions: [],
      shifts: [],
    };
  }
  return {
    prefilterId: asCopy ? undefined : prefilter.prefilterId,
    clientId: prefilter.clientId,
    campaign: prefilter.campaign ?? "",
    name: asCopy ? `${prefilter.name} (copia)` : prefilter.name,
    ageMin: toText(prefilter.ageMin),
    ageMax: toText(prefilter.ageMax),
    gender: prefilter.gender,
    maleQuota: toText(prefilter.maleQuota),
    requireVisa: prefilter.requireVisa,
    shiftExclusive: prefilter.shiftExclusive,
    workday: prefilter.workday ?? "",
    schedule: prefilter.schedule ?? "",
    notes: prefilter.notes ?? "",
    applicationsFrom: prefilter.applicationsFrom ?? "",
    order: String(prefilter.order ?? 0),
    active: asCopy ? true : prefilter.active,
    localityIds: prefilter.localities.map((locality) => locality.localityId),
    positions: [...prefilter.positions],
    shifts: [...prefilter.shifts],
  };
}

export function draftToPayload(draft: IPrefilterDraft) {
  const optionalNumber = (value: string) => (value.trim() === "" ? null : Number(value));
  return {
    ...(draft.prefilterId ? { prefilterId: draft.prefilterId } : {}),
    clientId: draft.clientId,
    campaign: draft.campaign,
    name: draft.name,
    ageMin: optionalNumber(draft.ageMin),
    ageMax: optionalNumber(draft.ageMax),
    gender: draft.gender,
    maleQuota: draft.gender === "CUOTA" ? optionalNumber(draft.maleQuota) : null,
    requireVisa: draft.requireVisa,
    shiftExclusive: draft.shiftExclusive,
    workday: draft.workday,
    schedule: draft.schedule,
    notes: draft.notes,
    applicationsFrom: draft.applicationsFrom || null,
    order: optionalNumber(draft.order) ?? 0,
    active: draft.active,
    localityIds: draft.localityIds,
    positions: draft.positions,
    shifts: draft.shifts,
  };
}
